// Shared cross-widget PostHog identity contract.
//
// The buy-again widget runs posthog-js and writes its real
// {session_id, distinct_id, device_id} to a sessionStorage key plus a
// same-document CustomEvent. The review widget (which does NOT bundle
// posthog-js) reads that triple so its /widget/reviews/views requests can
// stitch to the same PostHog session/device when both widgets share a page.
//
// sessionStorage (not localStorage) keeps us from reusing a stale, rotated
// session id from a previous session — buy-again rewrites a fresh value each
// load. When buy-again isn't on the page the reader simply yields nothing and
// the backend falls back to a stable `visitor_{visitorId}` distinct id.

const IDENTITY_KEY = "socialocean_posthog_identity";
const IDENTITY_EVENT = "social-ocean:posthog-identity";
const WAIT_MS = 2000;

export interface PostHogIdentity {
  distinctId?: string;
  sessionId?: string;
  deviceId?: string;
}

/** Read whatever buy-again has written this session (empty when absent). */
export function readPostHogIdentitySync(): PostHogIdentity {
  if (typeof window === "undefined") return {};
  try {
    const raw = sessionStorage.getItem(IDENTITY_KEY);
    if (!raw) return {};
    const j = JSON.parse(raw) as {
      session_id?: string | null;
      distinct_id?: string | null;
      device_id?: string | null;
    };
    return {
      distinctId: j.distinct_id ?? undefined,
      sessionId: j.session_id ?? undefined,
      deviceId: j.device_id ?? undefined,
    };
  } catch {
    return {};
  }
}

// Resolve at most once: if the key is already present, resolve immediately;
// otherwise wait for the CustomEvent (buy-again finishing posthog init) up to
// WAIT_MS, then resolve regardless. Cached so only the first caller waits.
let pending: Promise<void> | null = null;

export function ensurePostHogIdentity(): Promise<void> {
  if (pending) return pending;

  pending = new Promise<void>((resolve) => {
    if (typeof window === "undefined") {
      resolve();
      return;
    }

    // Already available — no need to wait.
    if (sessionStorage.getItem(IDENTITY_KEY)) {
      resolve();
      return;
    }

    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      window.removeEventListener(IDENTITY_EVENT, finish);
      clearTimeout(timer);
      resolve();
    };

    // The `storage` event never fires same-document (and not for
    // sessionStorage at all), so rely on buy-again's CustomEvent + a timeout.
    window.addEventListener(IDENTITY_EVENT, finish);
    const timer = setTimeout(finish, WAIT_MS);
  });

  return pending;
}
